import { assetRecordFirstImageSrc } from '../components/AssetGetByIdView';
import {
  catalogThumbForAssetId,
  normalizeAssetLookupKey,
} from './bookingAssetThumbnailCache';
import { prepareRecordForThumbnailLookup } from './assetImageResolve';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function coerceAssetId(raw: unknown): string | null {
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

function lookupKeysForRecord(record: Record<string, unknown>): string[] {
  const keys = new Set<string>();
  const add = (raw: unknown) => {
    const id = coerceAssetId(raw);
    if (!id) {
      return;
    }
    keys.add(id.toLowerCase());
    keys.add(normalizeAssetLookupKey(id));
  };
  add(record.id);
  add(record.Id);
  add(record.asset_id);
  add(record.assetId);
  add(record.AssetId);
  return Array.from(keys);
}

const thumbByKey = new Map<string, string>();
const venueByKey = new Map<string, Record<string, unknown>>();

function indexThumbnail(record: Record<string, unknown>, src: string): void {
  for (const key of lookupKeysForRecord(record)) {
    // Always overwrite — otherwise a successful image update keeps showing the first cached thumb.
    thumbByKey.set(key, src);
  }
}

function indexVenueRecord(record: Record<string, unknown>): void {
  const snap = prepareRecordForThumbnailLookup(record);
  for (const key of lookupKeysForRecord(record)) {
    venueByKey.set(key, snap);
  }
}

/** Register thumbnails from assets already loaded elsewhere (home, browse, favorites, owner updates). */
export function registerAssetThumbnailRecords(records: readonly Record<string, unknown>[]): void {
  for (const record of records) {
    if (!isPlainObject(record)) {
      continue;
    }
    indexVenueRecord(record);
    const src = assetRecordFirstImageSrc(prepareRecordForThumbnailLookup(record));
    if (src) {
      indexThumbnail(record, src);
    }
  }
}

/** Drop cached thumbs/venues for one asset after a successful image update. */
export function invalidateAssetThumbnailCacheForId(assetId: string): void {
  const trimmed = assetId.trim();
  if (!trimmed) {
    return;
  }
  for (const key of [trimmed.toLowerCase(), normalizeAssetLookupKey(trimmed)]) {
    thumbByKey.delete(key);
    venueByKey.delete(key);
  }
}

/** Register a single asset snapshot for booking confirmation venue lookup. */
export function registerAssetVenueRecord(record: Record<string, unknown>): void {
  if (!isPlainObject(record)) {
    return;
  }
  indexVenueRecord(record);
}

export function registeredAssetThumbnailById(assetId: string): string | null {
  return catalogThumbForAssetId(thumbByKey, assetId);
}

export function registeredAssetVenueById(assetId: string): Record<string, unknown> | null {
  const trimmed = assetId.trim();
  if (!trimmed) {
    return null;
  }
  const keys = [trimmed.toLowerCase(), normalizeAssetLookupKey(trimmed)];
  for (const key of keys) {
    const hit = venueByKey.get(key);
    if (hit) {
      return hit;
    }
  }
  return null;
}

/** Clears the in-memory store (tests only). */
export function resetAssetThumbnailStoreForTests(): void {
  thumbByKey.clear();
  venueByKey.clear();
}
